import {Component, inject, OnInit} from '@angular/core';
import {AppointmentDto} from '../../../../shared/dtos/appointment.dto';
import {PatientManagementService} from '../../patient-management.service';
import {MemoryService} from '../../../../core/services/memory.service';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {IconButtonComponent} from '../../../../shared/components/icon-button/icon-button.component';
import {Router} from '@angular/router';
import {DateTime} from 'luxon';
import {AppointmentQueryDto} from '../../dtos/appointment-query.dto';
import {MatRippleModule} from '@angular/material/core';
import {LocationQueryDto} from '../../../startup/dtos/location-query.dto';
import {LocationService} from '../../../../core/services/location.service';
import {LocationDetailDto} from '../../../startup/dtos/location-detail.dto';
import {MatIconModule} from '@angular/material/icon';
import {HttpErrorResponse} from '@angular/common/http';
import {ErrorService} from '../../../../core/services/error.service';
import {DatePipe} from '@angular/common';
import {LocationNamePipe} from '../../pipes/location-name-pipe';
import {MatMenuModule} from '@angular/material/menu';
import {MatDividerModule} from '@angular/material/divider';
import {LocalizedTextPipe} from '../../../../shared/pipes/localized-text.pipe';
import {OnlineBookingUrlsQueryDto} from '../../dtos/online-booking-urls-query.dto';
import {OnlineBookingUrlDto} from '../../dtos/online-booking-url.dto';
import {LocalizedTextDto} from '../../../../shared/dtos/localized-text.dto';
import {CustomDatePipe} from '../../../../shared/pipes/custom-date-pipe';

const defaultOnlineBookingName: LocalizedTextDto[] = [
  {language: 'de', value: 'Termin buchen'},
  {language: 'en', value: 'Book appointment'},
  {language: 'fr', value: 'Réserver le rendez-vous'},
  {language: 'it', value: 'Prenota appuntamento'},
];

@Component({
  selector: 'app-appointments',
  imports: [
    TranslocoPipe,
    IconButtonComponent,
    MatRippleModule,
    MatIconModule,
    LocationNamePipe,
    MatMenuModule,
    MatDividerModule,
    LocalizedTextPipe,
    CustomDatePipe,
  ],
  templateUrl: './appointments.component.html',
  styleUrl: './appointments.component.scss'
})
export class AppointmentsComponent implements OnInit {
  patientManagementService = inject(PatientManagementService);
  locationService = inject(LocationService);
  memoryService = inject(MemoryService);
  translocoService = inject(TranslocoService)
  router = inject(Router);
  errorService = inject(ErrorService);


  get appointmentDtos(): AppointmentDto[] {
    return this.patientManagementService.appointmentDtos ?? [];
  }

  ngOnInit() {
    if (this.patientManagementService.appointmentDtos == undefined) {
      const now = DateTime.now().toISO();

      const appointmentQueryDto: AppointmentQueryDto = {
        customerId: this.memoryService.customerId!,
        patientId: this.memoryService.patientId!,
        dateRangeStart: now,
      };

      this.patientManagementService.queryAppointments(appointmentQueryDto).subscribe({
        next: (appointmentDtos: AppointmentDto[]) => {
          this.patientManagementService.appointmentDtos = appointmentDtos.sort((a, b) => DateTime.fromISO(a.startTime).toMillis() - DateTime.fromISO(b.startTime).toMillis());

          const locationIds = appointmentDtos.map(a => a.locationId);
          this.loadLocations([...new Set(locationIds)]);
        },
        error: (error: HttpErrorResponse) => {
          this.errorService.handleError(error, this.translocoService.translate('error.title.appointments'));
        }
      })
    }

    if (this.patientManagementService.onlineBookingUrlDtos == undefined) {
      const onlineBookingUrlsQueryDto: OnlineBookingUrlsQueryDto = {
        patientId: this.memoryService.patientId,
        locationIds: (this.memoryService.locationId) ? [this.memoryService.locationId] : undefined,
      }

      this.patientManagementService.queryOnlineBookingUrls(onlineBookingUrlsQueryDto).subscribe({
        next: (onlineBookingUrlDtos: OnlineBookingUrlDto[]) => {
          this.patientManagementService.onlineBookingUrlDtos = onlineBookingUrlDtos.map(onlineBookingUrlDto => this.withDefaultOnlineBookingUrl(onlineBookingUrlDto));
        },
        error: (error: HttpErrorResponse) => {
          this.errorService.handleError(error, this.translocoService.translate('error.title.onlineBookingUrls'));
        }
      })
    }
  }

  loadLocations(locationIds: number[]) {
    if (locationIds.length == 0) {
      this.patientManagementService.locationDetailDtos = [];
      return;
    }

    const locationQueryDto: LocationQueryDto = {
      customerId: this.memoryService.customerId!,
      locationIds: locationIds,
    };

    this.locationService.queryLocationDetails(locationQueryDto).subscribe({
      next: (locationDetailsDtos: LocationDetailDto[]) => {
        this.patientManagementService.locationDetailDtos = locationDetailsDtos;
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.locations'));
      }
    })
  }

  withDefaultOnlineBookingUrl(onlineBookingUrlDto: OnlineBookingUrlDto): OnlineBookingUrlDto {
    if ((onlineBookingUrlDto.onlineBookingUrls && onlineBookingUrlDto.onlineBookingUrls.length > 0) || this.memoryService.customerId == undefined || this.memoryService.locationId == undefined) {
      return onlineBookingUrlDto;
    }

    const link = new URL('appointment-selection', document.baseURI);
    link.searchParams.set('customerId', this.memoryService.customerId!.toString());
    link.searchParams.set('locationId', this.memoryService.locationId!.toString());

    return {
      ...onlineBookingUrlDto,
      onlineBookingUrls: [{name: defaultOnlineBookingName, link: link.toString()}],
    };
  }

  open(appointmentDto: AppointmentDto) {
    this.patientManagementService.selectedAppointmentDto = appointmentDto;
    this.router.navigate(['/profile/appointment-details']);
  }
}
